<script lang="ts">
	import { format } from 'svelte-i18n';
	//Design system components
	import { AdvancedCard, AdvancedCardV2, Icon } from '@Shelly/shelly-design-system';
	//Helpers
	import { VIRTUAL_TEMPLATES } from 'src/helpers/virtual_helpers/template_helpers/virtual_template_config';
	import { getVirtualComponent } from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { getStatusIconByRole } from 'src/helpers/xt1_helpers/template_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Components
	import ThermostatModeModal from 'src/components/XT1/Thermostat/ThermostatModeModal.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { VCInterfaceStatus } from 'src/types/VirtualComponents';
	import type { Card } from 'src/components/SelectList/SelectList.types';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { I18nCtx } from 'src/helpers/virtual_helpers/template_helpers/virtual_template_types';

	export let device: Device,
		status: VCInterfaceStatus,
		useV2: boolean = false;

	const getThermModeOptions = () => {
		const enumConfigUI = getVirtualComponent(device.id, status?.component)?.config;
		const modeOptions = enumConfigUI?.meta?.ui?.titles || {};
		const templateIcons = VIRTUAL_TEMPLATES[status?.templateType]?.[status?.role]?.icons;
		const thermostatModeOptions: Card[] = [];
		for (const key in modeOptions) {
			const icon = templateIcons?.[key] || getStatusIconByRole(key) || undefined;
			thermostatModeOptions.push({
				id: key,
				name: modeOptions[key],
				icon,
				selected: false,
			});
			modeOptions[key] = $format(modeOptions[key], { default: modeOptions[key] });
		}
		return thermostatModeOptions;
	};

	const ctx: I18nCtx = { translate: (k, d) => $format(k, { default: d }) };
	const labelMode = VIRTUAL_TEMPLATES[status?.templateType]?.[status?.role]?.label
		? VIRTUAL_TEMPLATES[status?.templateType][status?.role].label(ctx)
		: status?.label;
	const subHeaders = {
		thermostat_mode: $format('label_thermostat_mode_choose', {
			default: 'Choose your thermostat work mode',
		}),
		fan_mode: $format('label_fan_choose', { default: 'Choose your fan speed' }),
	};
	const subHeader = subHeaders[status?.role] || '';

	const saveThermostatMode = async (value: string) => {
		waitForResponse = true;
		let res: SettingRequest = await gen2Request(device, 'enum.set', {
			id: status.id,
			value,
		});
		if (res?.isok) {
			selectedValue = value;
			onCloseTermModeModal();
		} else {
			onSettingsResponse(res, device);
		}
		waitForResponse = false;
	};

	const onCloseTermModeModal = () => {
		showTermModeModal = false;
	};

	let waitForResponse = false;
	let showTermModeModal = false;
	let selectedValue = status?.event;
</script>

{#if status}
	{#if useV2}
		<AdvancedCardV2
			class="mb-s"
			aria-label={status.value}
			title={status.value}
			subtitle={{
				value: labelMode,
				bold: false,
				semiBold: false,
				type: 'muted',
			}}
			avatar={{ icon: status.icon }}
			isCondensed
			isFocusable
			on:click={() => (showTermModeModal = true)}
		>
			<Icon class="align-self-center" icon="fas fa-chevron-right" size="s" slot="right" />
		</AdvancedCardV2>
	{:else}
		<AdvancedCard
			class="mb-s"
			aria-label={status.value}
			title={status.value}
			subtitle={{
				value: labelMode,
				bold: false,
				type: 'muted',
			}}
			avatar={{ icon: status.icon }}
			isCondensed
			isFocusable
			on:click={() => (showTermModeModal = true)}
		>
			<Icon class="align-self-center" icon="fas fa-chevron-right" size="s" slot="right" />
		</AdvancedCard>
	{/if}
	{#if showTermModeModal}
		<ThermostatModeModal
			on:save={(e) => {
				saveThermostatMode(e.detail['selectedValue']);
			}}
			on:close={onCloseTermModeModal}
			showModal={showTermModeModal}
			title={labelMode}
			heading={subHeader}
			{selectedValue}
			itemsList={getThermModeOptions()}
			isLoading={waitForResponse}
		/>
	{/if}
{/if}
